import { useState, useEffect } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { stripePromise } from "../utils/stripeConfig";
import { CryptoCheckoutForm } from "../components/CryptoCheckoutForm";
import { Bluelagoon } from "@bluelagoon";

export function CryptoCheckoutModal({
    isOpen,
    onClose,
    selectedPlan,
    isYearly,
    userId,
    t,
}) {
    const [clientSecret, setClientSecret] = useState(null);
    const [paymentIntentLoading, setPaymentIntentLoading] = useState(false);
    const [paymentIntentError, setPaymentIntentError] = useState(null);
    const [paymentDetails, setPaymentDetails] = useState(null);

    // Fetch PaymentIntent clientSecret when modal opens
    useEffect(() => {
        let isCancelled = false;

        if (isOpen && userId && selectedPlan && !clientSecret) {
            setPaymentIntentLoading(true);
            setPaymentIntentError(null);

            // Get the product ID from the plan
            const productId =
                selectedPlan.devProductId ||
                selectedPlan.productId ||
                selectedPlan.priceIdYearly ||
                selectedPlan.devPriceIdYearly;

            Bluelagoon.createCryptoPaymentIntent({ userId, productId, isYearly })
                .then((response) => {
                    if (isCancelled) return;

                    const responseData = response?.data?.data || response?.data;

                    if (responseData?.clientSecret) {
                        setClientSecret(responseData.clientSecret);
                        setPaymentDetails({
                            amount: responseData.amount,
                            planName: responseData.planName,
                            credits: responseData.credits,
                        });
                    } else {
                        console.error("Crypto payment intent response:", response);
                        setPaymentIntentError(
                            responseData?.error ||
                            response?.error ||
                            "Failed to initialize crypto payment"
                        );
                    }
                })
                .catch((error) => {
                    if (isCancelled) return;
                    console.error("Error creating crypto payment intent:", error);
                    setPaymentIntentError(
                        error.message || "Failed to initialize crypto payment"
                    );
                })
                .finally(() => {
                    if (!isCancelled) {
                        setPaymentIntentLoading(false);
                    }
                });
        }

        // Reset when modal closes
        if (!isOpen) {
            setClientSecret(null);
            setPaymentIntentError(null);
            setPaymentDetails(null);
        }

        return () => {
            isCancelled = true;
        };
    }, [isOpen, userId, selectedPlan, isYearly, clientSecret]);

    if (!isOpen || !selectedPlan) return null;

    // Elements options for PaymentElement with crypto
    const elementsOptions = clientSecret
        ? {
              clientSecret,
              appearance: {
                  theme: "stripe",
                  variables: {
                      colorPrimary: "#F7931A", // Bitcoin orange
                      colorBackground: "#ffffff",
                      colorText: "#1f2937",
                      colorDanger: "#ef4444",
                      fontFamily: "system-ui, sans-serif",
                      spacingUnit: "4px",
                      borderRadius: "8px",
                  },
              },
          }
        : undefined;

    return (
        <div
            className="fixed top-0 mt-0 left-0 right-0 bottom-0 inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 safe-area-inset billing-modal-overlay"
            style={{
                marginTop: "env(safe-area-inset-top)",
                position: "fixed",
                zIndex: 9999,
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            <div
                className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[95vh] overflow-hidden billing-modal-content"
                style={{
                    maxHeight: "min(95vh, 95dvh)",
                    position: "relative",
                    zIndex: 10000,
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 px-8 py-6">
                    <div className="flex justify-between items-start">
                        <div>
                            <h2 className="text-2xl font-bold text-white">
                                {t("billing.cryptoCheckout.title") || "Pay with Crypto"}
                            </h2>
                            <div className="flex items-center gap-2 mt-2">
                                <div className="flex items-center gap-1 text-white/90 text-sm">
                                    <span className="text-lg">₿</span>
                                    <span className="text-xs font-medium">
                                        {t("billing.cryptoCheckout.subtitle") ||
                                            "Pay with USDC stablecoin"}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-white/80 hover:text-white text-2xl leading-none min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation transition-colors"
                            aria-label={t("common.close") || "Close"}
                        >
                            x
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div
                    className="p-8 overflow-y-auto custom-scrollbar"
                    style={{
                        maxHeight: "calc(95vh - 120px)",
                    }}
                >
                    {/* No Auto-Renewal Notice */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                        <div className="flex items-start gap-3">
                            <span className="text-amber-500 text-xl">i</span>
                            <div>
                                <p className="text-sm font-medium text-amber-800">
                                    {t("billing.cryptoCheckout.noAutoRenewal") ||
                                        "Note: Crypto subscriptions do not auto-renew. We'll send you a reminder before expiry."}
                                </p>
                            </div>
                        </div>
                    </div>

                    {paymentIntentLoading && (
                        <div className="flex flex-col items-center justify-center py-12">
                            <svg
                                className="animate-spin h-8 w-8 text-orange-500 mb-4"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                ></circle>
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                            <p className="text-gray-600">
                                {t("billing.cryptoCheckout.processing") ||
                                    "Initializing crypto payment..."}
                            </p>
                        </div>
                    )}

                    {paymentIntentError && (
                        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                            <div className="flex items-start">
                                <svg
                                    className="w-5 h-5 text-red-500 mt-0.5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                                <div className="ml-3">
                                    <h4 className="text-sm font-bold text-red-800">
                                        {t("billing.errors.generic.title") ||
                                            "Payment Error"}
                                    </h4>
                                    <p className="text-sm text-red-700 mt-1">
                                        {paymentIntentError}
                                    </p>
                                    <button
                                        onClick={() => window.location.reload()}
                                        className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                                    >
                                        {t("common.tryAgain") || "Try again"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {!paymentIntentLoading &&
                        !paymentIntentError &&
                        clientSecret && (
                            <Elements stripe={stripePromise} options={elementsOptions}>
                                <CryptoCheckoutForm
                                    selectedPlan={selectedPlan}
                                    isYearly={isYearly}
                                    paymentDetails={paymentDetails}
                                    onSuccess={() => {
                                        onClose();
                                        window.location.reload();
                                    }}
                                    onCancel={onClose}
                                    t={t}
                                />
                            </Elements>
                        )}
                </div>
            </div>
        </div>
    );
}
